/**
 * @file: InboxHeader.tsx
 *
 * Inbox landing header band: optional burger (mobile), the "消息中心"
 * title with a total-unread count, and the search + settings actions.
 * `compact` renders a 56px band that matches the desktop detail/info
 * PaneHeader height (smaller title, vertically centred).
 */

import { Typography } from '@euler/app/components/typography';
import { Icon } from '@euler/components/Icomoon';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { Archive, Menu, Settings } from 'lucide-react-native';
import { FC, memo } from 'react';
import { TouchableOpacity, View } from 'react-native';

const kHitSlop = { top: 8, right: 8, bottom: 8, left: 8 };

export const InboxHeader: FC<{
  totalUnread?: number;
  compact?: boolean;
  onMenuPress?: () => void;
  onSearchPress?: () => void;
  onArchivePress?: () => void;
  onMorePress?: () => void;
}> = memo(
  ({
    totalUnread,
    compact,
    onMenuPress,
    onSearchPress,
    onArchivePress,
    onMorePress,
  }) => {
    const { rpx } = useResponsiveContext();
    const titleText =
      totalUnread && totalUnread > 0
        ? `消息中心 (${totalUnread > 99 ? '99+' : totalUnread})`
        : '消息中心';
    return (
      <View
        css={`
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          ${compact
            ? `height: ${rpx(56)}px; padding: 0 ${rpx(8)}px 0 ${rpx(20)}px;`
            : `padding: ${rpx(4)}px ${rpx(8)}px ${rpx(12)}px ${rpx(20)}px;`}
        `}
      >
        <View
          css={`
            flex: 1;
            flex-direction: row;
            align-items: center;
          `}
        >
          {onMenuPress ? (
            <TouchableOpacity
              onPress={onMenuPress}
              hitSlop={kHitSlop}
              css={`
                margin-right: ${rpx(10)}px;
                margin-left: -${rpx(4)}px;
              `}
            >
              <Menu size={rpx(24)} color="#111" />
            </TouchableOpacity>
          ) : null}
          <Typography.Text
            size={compact ? rpx(18) : rpx(28)}
            lineHeight={compact ? rpx(24) : rpx(36)}
            color="#111"
            style={{ fontFamily: FontFamily.NotoSans.Bold }}
          >
            {titleText}
          </Typography.Text>
        </View>
        <View
          css={`
            flex-direction: row;
            align-items: center;
          `}
        >
          <TouchableOpacity
            onPress={onSearchPress}
            disabled={!onSearchPress}
            hitSlop={kHitSlop}
            css={`
              padding: ${rpx(8)}px;
              opacity: ${onSearchPress ? 1 : 0.4};
            `}
          >
            <Icon name="search" size={rpx(20)} color="#111" />
          </TouchableOpacity>
          {onArchivePress ? (
            <TouchableOpacity
              onPress={onArchivePress}
              hitSlop={kHitSlop}
              css={`
                padding: ${rpx(8)}px;
                margin-left: ${rpx(4)}px;
              `}
            >
              <Archive size={rpx(20)} color="#111" />
            </TouchableOpacity>
          ) : null}
          <TouchableOpacity
            onPress={onMorePress}
            hitSlop={kHitSlop}
            css={`
              padding: ${rpx(8)}px;
              margin-left: ${rpx(4)}px;
            `}
          >
            <Settings size={rpx(20)} color="#111" />
          </TouchableOpacity>
        </View>
      </View>
    );
  },
);
